import React, { useState, useEffect } from "react";
import * as SubframeCore from "@subframe/core";
import { ProfileDetailItem } from "subframe/components/ProfileDetailItem";
import { WorkData, EmploymentStatus } from "../../../types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateEmployee } from "../../../services/retention";
import endpoints from "../../../constants/endpoints";
import { formatLocalDate } from "utils/helper";

interface WorkInformationProps {
  work: WorkData;
  employeeId?: number;
  onEdit?: () => void;
  onSave?: (updatedWork: WorkData) => void;
}

export const WorkInformation: React.FC<WorkInformationProps> = ({ work, employeeId, onEdit, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedWork, setEditedWork] = useState<WorkData>(work);
  const queryClient = useQueryClient();

  // Update editedWork when work prop changes (after successful API update)
  useEffect(() => {
    if (!isEditing) {
      setEditedWork(work);
    }
  }, [work, isEditing]);

  const updateEmployeeMutation = useMutation({
    mutationFn: updateEmployee,
    onSuccess: async (data) => {
      if (employeeId) {
        // Invalidate and refetch the employee data
        await queryClient.invalidateQueries({ queryKey: [endpoints.employee, employeeId] });
      }
      setIsEditing(false);
      onSave?.(editedWork);
    },
    onError: (error) => {
      // TODO: Add error handling here (show toast, etc.)
      console.error("Failed to update employee:", error);
    }
  });

  const handleEdit = () => {
    setIsEditing(true);
    setEditedWork(work);
    onEdit?.();
  };

  const handleSave = () => {
    if (!employeeId) {
      console.error("Employee ID is required for update");
      return;
    }

    const payload = {
      id: employeeId,
      location: editedWork.location,
      region: editedWork.region,
      hireDate: editedWork.hireDate,
      employmentType: editedWork.employmentType,
      employmentStatus: editedWork.employmentStatus,
      supervisor: editedWork.supervisor,
      supervisor_email: ""
    };

    updateEmployeeMutation.mutate(payload);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedWork(work);
  };

  const handleInputChange = (field: keyof WorkData, value: string) => {
    setEditedWork(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getEmploymentStatusConfig = (status: string) => {
    switch (status) {
      case EmploymentStatus.ACTIVE:
        return {
          icon: "FeatherCheckCircle",
          iconColor: "text-success-700",
          textColor: "text-success-700"
        };
      case EmploymentStatus.CLOSED:
        return {
          icon: "FeatherXCircle",
          iconColor: "text-error-700",
          textColor: "text-error-700"
        };
      case EmploymentStatus.LEAVE:
        return {
          icon: "FeatherClock",
          iconColor: "text-warning-700",
          textColor: "text-warning-700"
        };
      default:
        return {
          icon: "FeatherHelpCircle",
          iconColor: "text-neutral-500",
          textColor: "text-neutral-500"
        };
    }
  };

  const EditableField: React.FC<{
    icon: SubframeCore.IconName;
    label: string;
    value: string;
    field: keyof WorkData;
    className?: string;
    disabled?: boolean;
  }> = ({ icon, label, value, field, className, disabled = false }) => {
    if (isEditing) {
      return (
        <div className={`flex flex-col items-start gap-2 ${className}`}>
          <div className="flex items-center gap-2">
            <SubframeCore.Icon
              className="text-body font-body text-neutral-500"
              name={icon}
            />
            <span className="text-label font-label text-neutral-500">
              {label}
            </span>
          </div>
          <input
            type="text"
            value={editedWork[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 border border-neutral-border rounded-md text-body font-body text-default-font focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
              disabled ? 'bg-neutral-50 text-neutral-400 cursor-not-allowed' : ''
            }`}
            placeholder={disabled ? '' : `Enter ${label.toLowerCase()}`}
          />
        </div>
      );
    }

    return (
      <ProfileDetailItem
        className={className}
        variant="label"
        icon={icon}
        label={label}
        value={value || "N/A"}
      />
    );
  };

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm">
      <div className="flex items-center gap-4 pl-0 md:pl-3">
        <span className="text-subheader font-subheader text-default-font">
          Work information
        </span>
        {/*{!isEditing && onEdit && (*/}
        {/*  <button*/}
        {/*    onClick={handleEdit}*/}
        {/*    className="flex items-center gap-1 hover:opacity-80 transition-opacity"*/}
        {/*  >*/}
        {/*    <span className="text-label font-label text-brand-primary underline">*/}
        {/*      Edit*/}
        {/*    </span>*/}
        {/*    <SubframeCore.Icon*/}
        {/*      className="text-body font-body text-brand-primary"*/}
        {/*      name="FeatherEdit"*/}
        {/*    />*/}
        {/*  </button>*/}
        {/*)}*/}
        {isEditing && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={updateEmployeeMutation.isPending}
              className="flex items-center gap-1 hover:opacity-80 transition-opacity disabled:opacity-50"
            >
              <span className="text-label font-label text-success-700 underline">
                {updateEmployeeMutation.isPending ? "Saving..." : "Save"}
              </span>
              <SubframeCore.Icon
                className="text-body font-body text-success-700"
                name={updateEmployeeMutation.isPending ? "FeatherLoader" : "FeatherCheck"}
              />
            </button>
            <button
              onClick={handleCancel}
              disabled={updateEmployeeMutation.isPending}
              className="flex items-center gap-1 hover:opacity-80 transition-opacity disabled:opacity-50"
            >
              <span className="text-label font-label text-neutral-500 underline">
                Cancel
              </span>
              <SubframeCore.Icon
                className="text-body font-body text-neutral-500"
                name="FeatherX"
              />
            </button>
          </div>
        )}
      </div>
      <div className="flex w-full flex-col items-start gap-4">
        <div className="flex flex-col xl:flex-row items-start gap-4">
          <div className="flex flex-col items-start gap-4 w-full xl:w-auto">
            <EditableField
              icon="FeatherMapPin"
              label="Location"
              value={editedWork.location}
              field="location"
              className="h-auto w-full xl:w-60 flex-none"
            />
            <EditableField
              icon="FeatherGlobe"
              label="Region"
              value={editedWork.region}
              field="region"
              className="h-auto w-full xl:w-60 flex-none"
            />
          </div>
          <div className="flex flex-col items-start gap-4 w-full xl:w-auto">
            <EditableField
              icon="FeatherCalendar"
              label="Tenure"
              value={editedWork.tenure}
              field="tenure"
              className="h-auto w-full xl:w-60 flex-none"
              disabled={true}
            />
            <EditableField
              icon="FeatherBuilding"
              label="Hire Date"
              value={formatLocalDate(editedWork.hireDate)}
              field="hireDate"
              className="h-auto w-full xl:w-60 flex-none"
            />
          </div>
          <div className="flex flex-col items-start gap-4 w-full xl:w-auto">
            <EditableField
              icon="FeatherUser"
              label="Supervisor"
              value={editedWork.supervisor}
              field="supervisor"
              className="h-auto w-full xl:w-60 flex-none"
            />
            <EditableField
              icon="FeatherBriefcase"
              label="Employment Type"
              value={editedWork.employmentType}
              field="employmentType"
              className="h-auto w-full xl:w-60 flex-none"
            />
          </div>
        </div>
        <div className="flex w-full items-center gap-4 pl-0 md:pl-3">
          {(() => {
            const currentStatus = isEditing ? editedWork.employmentStatus : work.employmentStatus;
            const config = getEmploymentStatusConfig(currentStatus);
            return (
              <>
          <SubframeCore.Icon
                  className={`text-section-header font-section-header ${config.iconColor}`}
                  name={config.icon as SubframeCore.IconName}
          />
          <div className="flex grow shrink-0 basis-0 flex-col items-start">
            <span className="w-full break-words text-body font-body text-neutral-500">
              Employment Status
            </span>
                  {isEditing ? (
                    <select
                      value={editedWork.employmentStatus}
                      onChange={(e) => handleInputChange("employmentStatus", e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-border rounded-md text-body font-body focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-white"
                      style={{ color: editedWork.employmentStatus ? getEmploymentStatusConfig(editedWork.employmentStatus).textColor.replace('text-', '').replace('-700', '') : 'inherit' }}
                    >
                      <option value="">Select employment status</option>
                      <option value={EmploymentStatus.ACTIVE}>{EmploymentStatus.ACTIVE}</option>
                      <option value={EmploymentStatus.CLOSED}>{EmploymentStatus.CLOSED}</option>
                      <option value={EmploymentStatus.LEAVE}>{EmploymentStatus.LEAVE}</option>
                    </select>
                  ) : (
                    <span className={`w-full break-words text-body font-body ${config.textColor}`}>
                      {work.employmentStatus || "N/A"}
            </span>
                  )}
          </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}; 